import React, { useState } from 'react';
import { IonFab, IonFabButton, IonPopover } from '@ionic/react';
import { DuoCalendarIcon, DuoCommentPenIcon, DuoFileIcon, DuoNotebookIcon, PlusIcon } from '../theme/Icons';
import { MenuList } from '../components/Menu';
import { Text } from '../components/Text';
import { createUseStyles } from 'react-jss';
import { mediaMinWidth } from '../theme/utils';
import { SIDE_NAV_WIDTH } from './NavConstants';
import { CareMapState } from '../caremap/CareMapState';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { NEW_APPOINTMENT_ID } from '../appointments/AppointmentsState';
import { AppointmentEditorDrawer } from '../appointments/AppointmentEditorDrawer';
import { AddNoteWithQueryParamDrawer } from '../notes/add-note/AddNoteWithQueryParamDrawer';
import { AddCommunityPostWithQueryParamDrawer } from '../community/AddCommunityPostWithQueryParamDrawer';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { CheckinState } from '../caregiver/CheckinState';

export const FAB_WIDTH = 56;

const useStyles = createUseStyles({
    quickAdd: {
        marginBottom: 'var(--ion-safe-area-bottom, 0)',

        [mediaMinWidth.lg]: {
            bottom: 0,
            margin: (SIDE_NAV_WIDTH - FAB_WIDTH) / 2
        }
    },

    popover: {
        '--min-width': '350px',
        '& ion-list ion-item ion-text[slot="start"]': {
            marginInlineEnd: 12
        }
    }
});

export const QuickAddButton: React.FC = () => {

    const classes = useStyles();

    const [isOpen, setOpen] = useState(false);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const addNoteDrawer = useInstance(AddNoteWithQueryParamDrawer);
    const addPostDrawer = useInstance(AddCommunityPostWithQueryParamDrawer);
    const addApptDrawer = useInstance(AppointmentEditorDrawer);
    const checkinState = useInstance(CheckinState);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const careMap = careMapState.getMostRecentCaremap();

    const onActionClicked = () => {
        setOpen(false);
    };

    const track = (item: 'Journal' | 'Note' | 'Community Post' | 'Appointment') => {
        mixpanel.track('Quick Add Function Initiated', () => ({
            'Function': item
        }));
    };

    return (
        <>
            <IonFab vertical="bottom" horizontal="center" className={classes.quickAdd} data-id="quick-add-button">
                <IonFabButton
                    id="quick-add"
                    onClick={() => {
                        if (!isOpen) {
                            mixpanel.track('Quick Add Opened');
                        }
                        setOpen(prev => !prev);
                    }}
                >
                    <PlusIcon color="light" />
                </IonFabButton>
            </IonFab>

            <IonPopover
                keepContentsMounted
                trigger="quick-add"
                alignment="center"
                isOpen={isOpen}
                className={classes.popover}
                onDidDismiss={() => setOpen(false)}
            >
                <div style={{margin: 10, textAlign: 'center'}}>
                    <Text type="title"><span style={{fontSize: 16}}>Add</span></Text>
                </div>
                <MenuList onActionClicked={onActionClicked} actions={[
                    {
                        noLines: true,
                        text: 'Journal Entry',
                        Icon: DuoNotebookIcon,
                        handler: () => {
                            track('Journal');
                            const checkin = checkinState.getCheckinForToday();
                            checkinState.startEditing({ checkin, trackingFrom: 'Quick Add' });
                        }
                    },
                    {
                        noLines: true,
                        heading: true,
                        text: careMapState.getPatientFirstName() || 'Care'
                    },
                    {
                        text: 'Note',
                        Icon:  DuoFileIcon,
                        disabled: !careMap,
                        handler: () => {
                            track('Note');
                            addNoteDrawer.open({ careMapOnboardingStepComplete: 'add-note', trackingFrom: 'Quick Add' });
                        }
                    },
                    {
                        text: 'Community Post',
                        Icon: DuoCommentPenIcon,
                        disabled: !careMap,
                        handler: () => {
                            track('Community Post');
                            addPostDrawer.open({ trackingFrom: 'Quick Add' });
                        }
                    },
                    {
                        text: 'Appointment',
                        Icon: DuoCalendarIcon,
                        disabled: !careMap,
                        handler: () => {
                            track('Appointment');
                            addApptDrawer.open({ appointmentId: NEW_APPOINTMENT_ID, trackingFrom: 'Quick Add' });
                        }
                    }
                ]} />
            </IonPopover>
        </>
    );
};
